<template>
  <FullScreenPopup
    ref="popup"
    title="Вивести кошти з балансу"
    :lock-scroll="true"
  >
    <div class="withdrawal-request-popup">
      <div class="withdraw-sum">
        <LabeledTextInput
          ref="requestAmount"
          label="Сума (грн)"
          placeholder="Введіть суму до виведення"
          mask="number"
          :formatter="formatBalance"
          v-model="withdrawRequestSum"
          :error-messages="[
            !$v.withdrawRequestSum.required ? 'Введіть коректне значення від 1 до 100 000 грн (відповідно до суми на балансі)' : null,
            !$v.withdrawRequestSum.minValue ? 'Сума до виведення має бути не меншою 1 грн' : null,
            !$v.withdrawRequestSum.maxValue ? 'Сума до виведення має бути не більшою за 100 000 грн' : null,
            $v.withdrawRequestSum.$dirty && !$v.withdrawRequestSum.allowedSum ? 'Сума до виведення має бути меншою ніж баланс' : null,
          ].filter(Boolean)"
          @input="handleRequestAmount"
        />
      </div>

      <p class="description">{{ withdrawRequestConfig.commissionPercent }}% від суми виводу буде утримано згідно умов співпраці.
        Виплата здійснюється впродовж {{ getExpirationText(withdrawRequestConfig.processedDays, workingDaysForms) }}</p>

      <PopupSubtitle
        class="result-amount-title"
      >
        Сума до виведення:
      </PopupSubtitle>
      <p class="result-amount">{{ formatBalance(withdrawSum, '', ' грн') }}</p>

      <PopupSubtitle
        class="result-amount-title"
      >
        Дані отримувача
        <SmartLink
          class="edit-user-data"
          :method="handleEditUserData"
        >
          <PencilIcon />
        </SmartLink>
      </PopupSubtitle>

      <FormSkeleton
        :is-loading="!isUserDataReady"
        :elements="[
          { type: 'label', width: '40%' },
          { type: 'input' },
          { type: 'label', width: '35%' },
          { type: 'input' },
          { type: 'label', width: '60%' },
          { type: 'input' },
          { type: 'label', width: '35%' },
          { type: 'input' },
          { type: 'label', width: '30%' },
          { type: 'input' },
          { type: 'label', width: '40%' },
          { type: 'textarea' },
          { type: 'button', width: '120px', height: '20px' },
        ]"
      >
        <LabeledTextInput
          class="user-account"
          label="Рахунок отримувача"
          placeholder="Рахунок отримувача"
          :value="getRecipientAccount"
          :is-disabled="!userDataEditing"
          :formatter="formatIBAN"
          v-model="recipientAccount"
          :error-messages="[
            !$v.recipientAccount.required ? 'Введіть номер рахунку отримувача' : null,
            !$v.recipientAccount.minLength ? 'Введіть номер рахунку отримувача' : null,
          ].filter(Boolean)"
        />
        <LabeledTextInput
          class="user-bank"
          label="Назва банку "
          placeholder="Назва банку"
          :value="getRecipientBankName"
          :is-disabled="!userDataEditing"
          v-model="recipientBankName"
          :error-messages="[
            !$v.recipientBankName.required ? 'Введіть назву банку отримувача' : null,
            !$v.recipientBankName.minLength ? 'Введіть назву банку отримувача' : null,
          ].filter(Boolean)"
        />
        <LabeledTextInput
          class="user-name"
          label="Найменування отримувача"
          placeholder="Найменування отримувача"
          :value="getRecipientName"
          :is-disabled="!userDataEditing"
          v-model="recipientName"
          :error-messages="[
            !$v.recipientName.required ? 'Введіть імʼя особи отримувача' : null,
            !$v.recipientName.minLength ? 'Введіть імʼя особи отримувача' : null,
          ].filter(Boolean)"
        />
        <LabeledTextInput
          class="user-code"
          label="Код отримувача"
          placeholder="Код отримувача"
          :value="getRecipientCode"
          :is-disabled="!userDataEditing"
          v-model="recipientCode"
          :error-messages="[
            !$v.recipientCode.required ? 'Введіть код отримувача' : null,
            !$v.recipientCode.minLength ? 'Код отримувача не може бути менше 8 символів' : null,
            !$v.recipientCode.maxLength ? 'Код отримувача не може бути більше 10 символів' : null,
          ].filter(Boolean)"
        />
        <LabeledTextInput
          class="user-card-number"
          label="Номер картки"
          placeholder="Номер картки"
          :value="getRecipientBankCard"
          :is-disabled="!userDataEditing"
          mask="number"
          :formatter="formatCardNumber"
          v-model="recipientBankCard"
          :error-messages="[
            !$v.recipientBankCard.required ? 'Введіть номер картки отримувача' : null,
            !$v.recipientBankCard.minLength ? 'Номер картки отримувача не може бути менше 10 символів' : null,
            !$v.recipientBankCard.maxLength ? 'Номер картки отримувача не може бути більше 19 символів' : null,
          ].filter(Boolean)"
        />
        <LabeledTextAriaInput
          class="user-comment"
          label="Коментар"
          placeholder="Коментар"
          v-model="withdrawComment"
        />

        <div class="withdraw-controls">
          <Divider type="horizontal" />
          <div class="withdraw-controls-wrapper flex justify-center align-center">
            <BlueButton
              class="withdraw-controls__apply mr-2"
              size="small"
              @click="handleWithdrawRequest"
            >
              Застосувати
            </BlueButton>
            <WhiteButton
              class="withdraw-controls__cancel"
              @click="close"
            >
              Відмінити
            </WhiteButton>
          </div>
        </div>
      </FormSkeleton>
    </div>
  </FullScreenPopup>
</template>

<script>
import { maxLength, maxValue, minLength, minValue, required, requiredIf } from 'vuelidate/lib/validators';
import { mapActions, mapGetters } from 'vuex';
import { withdrawRequest } from '@/features/account-balance/config';
import { workingDays } from '@/shared/config/expiration-forms';
import { balanceFormatter } from '@/shared/lib/balance';
import { cardNumberFormatter, ibanFormatter } from '@/shared/lib/currency';
import { expirationText } from '@/shared/lib/string';
import { Divider } from '@/shared/ui/divider';
import { BlueButton, FormSkeleton, LabeledTextAriaInput, LabeledTextInput, WhiteButton } from '@/shared/ui/form';
import { PopupSubtitle } from '@/shared/ui/full-screen-popup';
import { PencilIcon } from '@/shared/ui/icons';
import { SmartLink } from '@/shared/ui/link';
import { FullScreenPopup } from '@/shared/ui/popup';

export default {
  data() {
    return {
      withdrawRequestSum: 1,
      withdrawSum: 0,
      withdrawComment: '',
      recipientAccount: '',
      recipientBankName: '',
      recipientName: '',
      recipientCode: '',
      recipientBankCard: '',
      userDataEditing: false,
    };
  },
  created() {
    this.setRecipientData();
  },
  validations: {
    withdrawRequestSum: {
      required,
      minValue: minValue(1),
      maxValue: maxValue(100000),
      allowedSum: function (value) { return value <= this.getBalanceAmount; },
    },
    recipientAccount: {
      required,
      minLength: minLength(2),
    },
    recipientBankName: {
      required,
      minLength: minLength(2),
    },
    recipientName: {
      required,
      minLength: minLength(2),
    },
    recipientCode: {
      required,
      minLength: minLength(8),
      maxLength: maxLength(10),
    },
    recipientBankCard: {
      required: requiredIf(function() { return this.getRecipientType === 0; }),
      minLength: minLength(10),
      maxLength: maxLength(19),
    },
  },
  mixins: [ balanceFormatter, expirationText, ibanFormatter, cardNumberFormatter ],
  components: { FullScreenPopup, LabeledTextInput, PopupSubtitle, PencilIcon, LabeledTextAriaInput, BlueButton,
    WhiteButton, Divider, SmartLink, FormSkeleton },
  computed: {
    ...mapGetters('currentUserModule', [
      'isUserDataReady', 'getRecipientAccount', 'getRecipientBankName', 'getRecipientBankName', 'getRecipientCode',
      'getRecipientBankCard', 'getRecipientName', 'getRecipientType', 'getCurrentUserId',
    ]),
    ...mapGetters('accountBalanceModule', ['getBalanceAmount']),
    withdrawRequestConfig: () => withdrawRequest,
    workingDaysForms: () => workingDays.forms,
  },
  methods: {
    ...mapActions('withdrawalRequestModule', ['createWithdrawalRequest']),
    ...mapActions('notificationModule', ['showNotification']),
    open() { this.$refs.popup.open(); },
    close() { this.$refs.popup.close(); },
    setRecipientData() {
      if (this.isUserDataReady) {
        this.recipientAccount = this.getRecipientAccount;
        this.recipientBankName = this.getRecipientBankName;
        this.recipientName = this.getRecipientName;
        this.recipientCode = this.getRecipientCode;
        this.recipientBankCard = this.getRecipientBankCard;
      }
    },
    handleRequestAmount() {
      if (Number.isNaN(this.withdrawRequestSum) || Number(this.withdrawRequestSum) > this.getBalanceAmount) {
        this.withdrawSum = 0;

        return;
      }

      this.withdrawSum = ((this.withdrawRequestSum * (100 - Number(withdrawRequest.commissionPercent))) / 100).toFixed(2);

    },
    handleEditUserData() {
      this.userDataEditing = !this.userDataEditing;
    },
    async handleWithdrawRequest() {
      if (this.$v.$invalid) {
        this.$v.$touch();
      }

      const response = await this.createWithdrawalRequest({
        userId: this.getCurrentUserId,
        amount: this.withdrawRequestSum,
        amountActual: this.withdrawSum,
        comment: this.withdrawComment,
        recipientData: {
          recipientAccount: this.recipientAccount,
          recipientBankName: this.recipientBankName,
          recipientCode: this.recipientCode,
          recipientName: this.recipientName,
          recipientBankCard: this.recipientBankCard,
        },
      });

      if (response.status === 'success') {
        this.showNotification({
          type: 'info',
          title: 'Запит сформовано',
          message: 'Запит на зняття коштів вдало відправлено',
        });
      }

      this.close();
    },
  },
  watch: {
    isUserDataReady() {
      this.setRecipientData();
    },
  }
};
</script>

<style lang="scss" scoped>
.withdrawal-request-popup {
  max-width: 450px;

  @media (max-width: 550px) {
    min-width: auto;
  }
}

.description {
  color: #FB923C;
  text-align: center;
  margin-bottom: 16px;
}

.result-amount-title {
  position: relative;
  text-align: center;
  margin-bottom: 16px;

  .edit-user-data {
    position: absolute;
    right: 0;
    opacity: 1;

    &:hover {
      opacity: 0.6;
    }
  }
}

.result-amount {
  color: #FB923C;
  text-align: center;
  font-weight: 600;
  font-size: 24px;
  margin-bottom: 16px;
}

.user-comment {
  min-height: 130px;
}

.error {
  color: #FF3B30;
  text-align: center;
  margin-bottom: 16px;
}

</style>
